import React, { useCallback, useEffect, useState } from "react";
import {
  Anchor,
  Box,
  Divider,
  Group,
  List,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { Alert, Button } from "@xplatform/design-system";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconCheck,
  IconDownload,
  IconInfoCircle,
  IconLock,
  IconUpload,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { reviewsApi } from "@services/reviewsApi";
import { ApiError } from "@models/error";
import { filenameFromKey, triggerDownload } from "@utils/download";
import type { HistoryEvent, ReviewDownload, ValidateResponse } from "@models/reviews";
import { ReviewStatusBadge } from "./ReviewStatusBadge";
import { SupersessionPanel } from "./SupersessionPanel";
import { UploadHistoryTable } from "./UploadHistoryTable";
import { ReviewFileDrop } from "./ReviewFileDrop";

const MAX_UPLOAD_SIZE = 100 * 1024 * 1024; // 100 MB, same limit as the backend

// The checks /reviews/validate performs, in the order it performs them. Keep in sync with
// backend/reviews_api/router.py::validate_upload if the checks ever change.
const VALIDATION_RULES = [
  "filename",
  "columns",
  "rowOrder",
  "onlyRightWrong",
  "values",
  "complete",
] as const;

interface Props {
  download: ReviewDownload;
  onBack: () => void;
  /** Called after an upload or cancel so the list (and this file's status) is refreshed. */
  onChanged: () => void;
}

/** Per-file detail view (PBI #781340): header, history timeline, upload form or imported notice. */
export const ReviewFileDetail: React.FC<Props> = ({ download, onBack, onChanged }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const status = download.review_status;
  const uploadAllowed = status === "outstanding" || status === "pending";

  // ── History ───────────────────────────────────────────────────────────────
  const [events, setEvents] = useState<HistoryEvent[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    setHistoryError(null);
    try {
      const resp = await reviewsApi.getHistory(download.key);
      setEvents(resp.events);
    } catch (e) {
      setHistoryError(e instanceof Error ? e.message : t("common.error"));
    }
  }, [download.key, t]);

  useEffect(() => {
    setEvents(null);
    void loadHistory();
  }, [loadHistory]);

  // Verdicts the import could not apply (PBI #936072) — from the latest `imported` entry.
  const droppedImport = [...(events ?? [])]
    .reverse()
    .find((e) => e.action === "imported" && (e.failed_rows_count ?? 0) > 0);

  // ── Downloads ─────────────────────────────────────────────────────────────
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const runDownload = async (key: string, getUrl: (k: string) => Promise<{ url: string }>) => {
    setDownloadingKey(key);
    setDownloadError(null);
    try {
      const { url } = await getUrl(key);
      await triggerDownload(url, filenameFromKey(key));
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setDownloadingKey(null);
    }
  };

  // ── Upload ────────────────────────────────────────────────────────────────
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<ValidateResponse | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleFileChange = (f: File | null) => {
    setFile(f);
    setFileError(null);
    setResult(null);
    setUploadError(null);
    if (!f) return;
    if (f.size > MAX_UPLOAD_SIZE) {
      setFileError(t("reviews.upload.fileTooLarge"));
    } else if (f.name !== download.filename) {
      setFileError(t("reviews.upload.filenameMismatch", { expected: download.filename }));
    }
  };

  const handleSubmit = async () => {
    if (!file || fileError) return;
    setUploading(true);
    setUploadError(null);
    setResult(null);
    try {
      const init = await reviewsApi.getUploadUrl(download.kpi, file.name);
      await reviewsApi.uploadToS3(init.url, file);
      const validated = await reviewsApi.validate(init.upload_key, download.key);
      setResult(validated);
      setFile(null);
      onChanged();
      void loadHistory();
    } catch (e) {
      // The API's message says what is wrong and where (row, column, values) — show it as-is.
      // The texts are pinned by e2e/contracts/validation-rejections.json.
      setUploadError(e instanceof Error ? e.message : t("common.error"));
      // The release was imported after this page was loaded (the API refuses it): reload so
      // the page switches to the "imported – cannot be changed" notice.
      if (e instanceof ApiError && e.errorCode === "upload.alreadyImported") {
        onChanged();
        void loadHistory();
      }
    } finally {
      setUploading(false);
    }
  };

  // ── Cancel a pending upload ───────────────────────────────────────────────
  const [cancelKey, setCancelKey] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const handleCancel = async () => {
    if (!cancelKey) return;
    setCancelling(true);
    setCancelError(null);
    try {
      await reviewsApi.cancelUpload(cancelKey);
      setCancelKey(null);
      onChanged();
      void loadHistory();
    } catch (e) {
      setCancelError(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      <Stack gap="lg">
        <Group>
          <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />} onClick={onBack}>
            {t("reviews.detail.back")}
          </Button>
        </Group>

        {/* ── Header ── */}
        <Paper withBorder p="lg">
          <Group justify="space-between" align="flex-start">
            <Box>
              <Title order={3}>{download.period}</Title>
              <Text size="sm" c="dimmed" mt={4}>
                {download.filename}
              </Text>
              <Text size="sm" mt={4}>
                {t("reviews.detail.rows", {
                  count: download.row_count != null && download.row_count >= 0 ? download.row_count : 0,
                })}
              </Text>
            </Box>
            <Stack gap="sm" align="flex-end">
              <ReviewStatusBadge status={status} size="lg" />
              <Button
                size="xs"
                variant="light"
                color="green"
                leftSection={<IconDownload size={14} />}
                loading={downloadingKey === download.key}
                onClick={() => void runDownload(download.key, reviewsApi.getDownloadUrl)}
              >
                {t("reviews.detail.downloadOriginal")}
              </Button>
            </Stack>
          </Group>
          {downloadError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" mt="md" withCloseButton onClose={() => setDownloadError(null)}>
              {downloadError}
            </Alert>
          )}
        </Paper>

        {/* ── History ── */}
        <Paper withBorder p="lg">
          <Stack gap="sm">
            <Title order={4}>{t("reviews.detail.historyTitle")}</Title>
            {historyError && (
              <Alert icon={<IconAlertCircle size={16} />} color="red">
                {historyError}
              </Alert>
            )}
            {events === null && !historyError ? (
              <Group justify="center" py="md">
                <Loader size="sm" />
              </Group>
            ) : events && events.length === 0 ? (
              <Text size="sm" c="dimmed">
                {t("reviews.detail.noHistory")}
              </Text>
            ) : events ? (
              <UploadHistoryTable
                events={events}
                downloadingKey={downloadingKey}
                onDownload={(key) => void runDownload(key, reviewsApi.getUploadedFileUrl)}
                onCancel={(key) => setCancelKey(key)}
              />
            ) : null}
          </Stack>
        </Paper>

        {/* ── New upload, or the imported notice ── */}
        <Paper withBorder p="lg">
          {!uploadAllowed ? (
            <Stack gap="md">
              {/* Set when an upload was refused because the release got imported meanwhile. */}
              {uploadError && (
                <Alert icon={<IconAlertCircle size={16} />} color="red">
                  {uploadError}
                </Alert>
              )}
              {droppedImport && (
                <Alert icon={<IconAlertCircle size={16} />} color="orange" data-testid="dropped-verdicts">
                  <Stack gap="xs" align="flex-start">
                    <Text size="sm">
                      {t("reviews.detail.droppedVerdicts", { count: droppedImport.failed_rows_count })}
                    </Text>
                    {droppedImport.failed_rows_key && (
                      <Button
                        size="xs"
                        variant="light"
                        color="orange"
                        leftSection={<IconDownload size={14} />}
                        loading={downloadingKey === droppedImport.failed_rows_key}
                        onClick={() =>
                          void runDownload(droppedImport.failed_rows_key as string, reviewsApi.getUploadedFileUrl)
                        }
                      >
                        {t("reviews.detail.downloadDropped")}
                      </Button>
                    )}
                  </Stack>
                </Alert>
              )}
              <Alert icon={<IconLock size={16} />} color="teal">
                {status === "imported" ? t("reviews.detail.importedNotice") : t("reviews.detail.emptyNotice")}
                {status === "imported" && (
                  <>
                    {" "}
                    {t("reviews.detail.importedCorrectHint")}{" "}
                    <Anchor component="button" size="sm" onClick={() => navigate("../correct", { relative: "path" })}>
                      {t("reviews.detail.importedCorrectLink")}
                    </Anchor>
                    .
                  </>
                )}
              </Alert>
            </Stack>
          ) : (
            <Stack gap="md">
              <Title order={4}>{t("reviews.upload.newUploadTitle")}</Title>

              {status === "pending" && (
                <Alert icon={<IconAlertCircle size={16} />} color="blue">
                  {t("reviews.upload.existingWillReplace")}
                </Alert>
              )}
              {result && (
                <Alert icon={<IconCheck size={16} />} color="green">
                  {t("reviews.upload.success")}{" "}
                  <Text span fw={600}>
                    {t("reviews.upload.filled", { filled: result.filled, total: result.total })}
                  </Text>
                </Alert>
              )}
              {result?.supersession && <SupersessionPanel supersession={result.supersession} />}
              {uploadError && (
                <Alert icon={<IconAlertCircle size={16} />} color="red" withCloseButton onClose={() => setUploadError(null)}>
                  {uploadError}
                </Alert>
              )}

              <ReviewFileDrop
                value={file}
                onChange={handleFileChange}
                error={fileError}
                expectedFilename={download.filename}
              />
              {file && !fileError && (
                <Alert icon={<IconCheck size={14} />} color="green" variant="light" py="xs">
                  {t("reviews.upload.filenameMatch")}
                </Alert>
              )}
              <Group justify="flex-end">
                <Button
                  leftSection={<IconUpload size={14} />}
                  loading={uploading}
                  disabled={!file || !!fileError}
                  onClick={() => void handleSubmit()}
                >
                  {t("reviews.upload.uploadAndValidate")}
                </Button>
              </Group>

              <Divider />
              <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light" title={t("reviews.rules.title")}>
                <List size="sm" spacing="xs">
                  {VALIDATION_RULES.map((rule) => (
                    <List.Item key={rule}>
                      <Text size="sm">{t(`reviews.rules.${rule}.rule`)}</Text>
                      <Text size="xs" c="dimmed">
                        {t(`reviews.rules.${rule}.example`)}
                      </Text>
                    </List.Item>
                  ))}
                </List>
              </Alert>
            </Stack>
          )}
        </Paper>
      </Stack>

      <Modal
        opened={cancelKey !== null}
        onClose={() => setCancelKey(null)}
        title={t("reviews.history.cancelModal.title")}
        centered
      >
        <Stack gap="md">
          <Text size="sm">{t("reviews.history.cancelModal.confirm")}</Text>
          {cancelError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              {cancelError}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setCancelKey(null)}>
              {t("common.cancel")}
            </Button>
            <Button color="red" loading={cancelling} onClick={() => void handleCancel()}>
              {t("common.delete")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
